import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { Plus, Search, Upload, X } from 'lucide-react';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Input } from '@freelancer-os/ui/src/components/ui/input';
import { Label } from '@freelancer-os/ui/src/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@freelancer-os/ui/src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@freelancer-os/ui/src/components/ui/dialog';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import type { CreateClientInput } from '@freelancer-os/validation';
import { useClients, useCreateClient, useImportClientsCsv } from '../../hooks/useClients';
import { ClientTags } from './components/ClientTags';

const SEARCH_DEBOUNCE_MS = 300;

export function ClientsPage() {
  const { t } = useTranslation('clients');
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const tag = searchParams.get('tag') ?? '';

  const [searchInput, setSearchInput] = useState(search);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Debounce search input → URL
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchInput) next.set('search', searchInput);
      else next.delete('search');
      setSearchParams(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const { data: clients, isLoading } = useClients({
    search: search || undefined,
    tag: tag || undefined,
  });

  // Aggregate unique tags from current results for the filter chip bar
  const allTags = useMemo(() => {
    if (!clients) return [];
    const set = new Set<string>();
    for (const c of clients) {
      for (const t of c.tags) set.add(t);
    }
    return Array.from(set).sort();
  }, [clients]);

  const setTag = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('tag', next);
    else params.delete('tag');
    setSearchParams(params, { replace: true });
  };

  const clearAll = () => {
    setSearchInput('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    params.delete('tag');
    setSearchParams(params, { replace: true });
  };

  const hasActiveFilter = Boolean(search || tag);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const importCsv = useImportClientsCsv();
  const handleCsvSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    importCsv.mutate(file, {
      onSuccess: (result) => {
        toast.success(
          t('import.success', {
            created: result.created,
            skipped: result.skippedDuplicates,
            errors: result.errors.length,
          }),
        );
      },
      onError: (err) => {
        toast.error(err.message || t('import.error'));
      },
    });
  };

  return (
    <div className="p-8 max-w-6xl mx-auto w-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('list.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleCsvSelected}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importCsv.isPending}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {importCsv.isPending ? t('import.uploading') : t('import.button')}
          </Button>
          <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" /> {t('list.add')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('list.search')}
          className="pl-9"
        />
      </div>

      {/* Tag filter chips */}
      {(allTags.length > 0 || hasActiveFilter) && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {allTags.map((tagName) => {
            const isActive = tag === tagName;
            return (
              <button
                key={tagName}
                type="button"
                onClick={() => setTag(isActive ? null : tagName)}
                className={cn(
                  'inline-flex items-center h-7 px-3 rounded-full text-xs font-medium border transition-colors',
                  isActive
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-surface-0 text-surface-600 border-surface-200 hover:bg-surface-50',
                )}
              >
                {tagName}
              </button>
            );
          })}
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 h-7 px-3 text-xs text-surface-500 hover:text-surface-700"
            >
              <X className="w-3 h-3" /> {t('list.clearFilter')}
            </button>
          )}
        </div>
      )}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('list.name')}</TableHead>
              <TableHead>{t('list.email')}</TableHead>
              <TableHead>{t('list.zaloPhone')}</TableHead>
              <TableHead>{t('list.added')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  {t('list.loading')}
                </TableCell>
              </TableRow>
            ) : !clients || clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  {hasActiveFilter ? t('list.noResults') : t('list.empty')}
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id} className="cursor-pointer">
                  <TableCell>
                    <Link to={`/clients/${client.id}`} className="block">
                      <div className="font-medium text-surface-900">{client.name}</div>
                      {client.tags.length > 0 && (
                        <div className="mt-1">
                          <ClientTags tags={client.tags} mode="read" />
                        </div>
                      )}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/clients/${client.id}`} className="block text-surface-600">
                      {client.email || '—'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/clients/${client.id}`} className="block text-surface-600">
                      {client.zaloPhone || '—'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/clients/${client.id}`} className="block text-surface-600">
                      {new Date(client.createdAt).toLocaleDateString('vi-VN')}
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CreateClientDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>
  );
}

function CreateClientDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('clients');
  const { mutate: createClient, isPending } = useCreateClient();
  const [formData, setFormData] = useState<CreateClientInput>({
    name: '',
    email: '',
    zaloPhone: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createClient(formData, {
      onSuccess: () => {
        onOpenChange(false);
        setFormData({ name: '', email: '', zaloPhone: '' });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('create.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('create.namePlaceholder')}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('create.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('create.emailPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="zaloPhone">{t('create.zaloPhone')}</Label>
              <Input
                id="zaloPhone"
                value={formData.zaloPhone || ''}
                onChange={(e) => setFormData({ ...formData, zaloPhone: e.target.value })}
                placeholder={t('create.zaloPhonePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('create.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !formData.name}>
              {isPending ? t('create.submitting') : t('create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
